/**
 * Admin panel permissions (D74, D77). Shared by the API (enforcement), the admin panel (what to
 * show) and the database (a CHECK constraint keeps owner-only permissions out of custom roles —
 * keep OWNER_ONLY_PERMISSIONS in sync with migration `admin_roles`).
 *
 * The fixed Owner role has every permission. Custom roles may hold any permission that is NOT
 * owner-only.
 */
export const ADMIN_PERMISSIONS = {
    // ── Can be given to custom roles ──
    "dashboard.view": { label: "View dashboard and alerts", group: "View", ownerOnly: false },
    "users.view": { label: "View users (list and profile)", group: "View", ownerOnly: false },
    "users.ledger": { label: "View balances and ledger", group: "View", ownerOnly: false },
    "queues.view": { label: "View withdrawal and deposit queues", group: "View", ownerOnly: false },
    "swaps.view": { label: "View swaps", group: "View", ownerOnly: false },
    "wallets.view": { label: "View wallets and reconciliation", group: "View", ownerOnly: false },
    "vault_recovery.view": { label: "View vault recovery requests", group: "View", ownerOnly: false },
    "audit.view": { label: "View audit log", group: "View", ownerOnly: false },
    "reports.view": { label: "View deal reports and export CSV", group: "View", ownerOnly: false },
    "withdrawals.review": { label: "Approve or reject withdrawals", group: "Act", ownerOnly: false },
    "users.manage": { label: "Block/unblock users, set KYC flag", group: "Act", ownerOnly: false },
    "emergency.pause": {
        label: "Emergency: pause withdrawals/swaps, lock signer",
        group: "Act",
        ownerOnly: false,
    },
    "messages.manage": { label: "Manage system messages to customers", group: "Act", ownerOnly: false },
    "support.chat": { label: "Answer support chats", group: "Act", ownerOnly: false },
    // ── Owner only (fixed) ──
    "limits.edit": { label: "Change global, swap and per-user limits", group: "Owner", ownerOnly: true },
    "pause.resume": { label: "Resume withdrawals or swaps", group: "Owner", ownerOnly: true },
    "signer.unlock": { label: "Unlock the signer", group: "Owner", ownerOnly: true },
    "cold.send": { label: "Move funds to cold", group: "Owner", ownerOnly: true },
    "vault_recovery.decide": { label: "Approve or reject vault recovery", group: "Owner", ownerOnly: true },
    "vault_recovery.reveal": {
        label: "Read a client's recovery words (with the recovery passphrase)",
        group: "Owner",
        ownerOnly: true,
    },
    "withdrawals.mark_failed": {
        label: "Mark a stuck withdrawal failed (refund)",
        group: "Owner",
        ownerOnly: true,
    },
    "staff.manage": { label: "Manage staff and roles", group: "Owner", ownerOnly: true },
};
export const ALL_ADMIN_PERMISSIONS = Object.keys(ADMIN_PERMISSIONS);
export const OWNER_ONLY_PERMISSIONS = ALL_ADMIN_PERMISSIONS.filter((p) => ADMIN_PERMISSIONS[p].ownerOnly);
export const DELEGABLE_PERMISSIONS = ALL_ADMIN_PERMISSIONS.filter((p) => !ADMIN_PERMISSIONS[p].ownerOnly);
export function isAdminPermission(value) {
    return Object.hasOwn(ADMIN_PERMISSIONS, value);
}
/** What a role effectively grants: everything for the Owner, otherwise its delegable permissions. */
export function effectivePermissions(role) {
    if (role.isOwner)
        return [...ALL_ADMIN_PERMISSIONS];
    return DELEGABLE_PERMISSIONS.filter((p) => role.permissions.includes(p));
}
/** Starter roles created by the migration (the owner may edit or delete them). */
export const STARTER_ROLES = [
    {
        name: "Support",
        description: "Helps customers: sees users and answers chats.",
        permissions: ["dashboard.view", "users.view", "support.chat"],
    },
    {
        name: "Operations",
        description: "Runs the queues: approves withdrawals and can pause in an emergency.",
        permissions: ["dashboard.view", "users.view", "queues.view", "withdrawals.review", "emergency.pause"],
    },
    {
        name: "Viewer",
        description: "Read-only access to every page.",
        permissions: [
            "dashboard.view",
            "users.view",
            "users.ledger",
            "queues.view",
            "swaps.view",
            "wallets.view",
            "vault_recovery.view",
            "audit.view",
        ],
    },
];
//# sourceMappingURL=admin-permissions.js.map